import { get, writable } from "svelte/store";
import { SearchService } from "$/services/SearchService";
import { debounce } from "$/utils/Utils";
import { actions as GlobalSearchActions } from "$/components/globalSearch/GlobalSearch.svelte";
import { snippetsUtils } from "$/utils/snippetsUtils";
const store = () => {
    const { subscribe, set, update } = writable({
        initialSearch: true,
        showModal: false,
        loading: false,
        semanticSearch: false,
        webSearch: false,
        searchQuery: "",
        snippets: [],
        comments: [],
        topics: [],
        suggestions: [],
    });
    const filterData = (items, query) => {
        return ((items === null || items === void 0 ? void 0 : items.filter((item) => {
            var _a, _b, _c, _d;
            return ((_a = item.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(query.toLowerCase())) ||
                ((_b = item.content) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(query.toLowerCase())) ||
                ((_c = item.comment) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes(query.toLowerCase())) ||
                ((_d = item.url) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes(query.toLowerCase()));
        })) || []);
    };
    const filterSnippetsAndSortByArchived = (items, query) => {
        return snippetsUtils.sortByArchived(filterData(items, query));
    };
    const debouncedSearch = debounce(async (query, tenantId, filteredComments, filteredSnippets, filteredTopics, semantic, webSearch) => {
        var _a, _b;
        // Have to use try/catch here because sometimes the search service throws an error
        // When for example user types "How to"
        try {
            if (webSearch) {
                const suggestions = await SearchService.getSuggestions(query, 30);
                update((state) => (Object.assign(Object.assign({}, state), { suggestions, loading: false })));
            }
            else if (!semantic) {
                const searchResults = await SearchService.search(query, false, tenantId);
                const newSnippets = (_a = searchResults.snippets) === null || _a === void 0 ? void 0 : _a.filter((s) => !filteredSnippets.some((fs) => fs.id === s.id));
                const allSnippets = snippetsUtils.sortByArchived([
                    ...filteredSnippets,
                    ...newSnippets,
                ]);
                const newComments = (_b = searchResults.comments) === null || _b === void 0 ? void 0 : _b.filter((c) => !filteredComments.some((fc) => fc.id === c.id));
                const allComments = [...filteredComments, ...newComments];
                update((state) => (Object.assign(Object.assign({}, state), { comments: allComments, snippets: allSnippets, topics: filteredTopics, suggestions: [], loading: false })));
            }
            else {
                clear();
                const searchResult = await SearchService.search(`${query}`, true, tenantId);
                update((state) => {
                    var _a, _b;
                    return (Object.assign(Object.assign({}, state), { comments: (_a = searchResult.comments) === null || _a === void 0 ? void 0 : _a.slice(0, 30), snippets: (_b = searchResult.snippets) === null || _b === void 0 ? void 0 : _b.slice(0, 30), topics: [], suggestions: [], loading: false }));
                });
            }
        }
        catch (error) {
            update((state) => (Object.assign(Object.assign({}, state), { loading: false })));
        }
    }, 1000, false);
    const search = (query, topics, tenantId, activeTopicSnippets, semantic = false, webSearch = false) => {
        update((state) => (Object.assign(Object.assign({}, state), { initialSearch: false, searchQuery: query })));
        topics = topics !== null && topics !== void 0 ? topics : [];
        activeTopicSnippets = activeTopicSnippets !== null && activeTopicSnippets !== void 0 ? activeTopicSnippets : [];
        const filteredTopics = filterData(topics, query);
        const filteredSnippets = filterSnippetsAndSortByArchived(activeTopicSnippets, query);
        const filteredComments = activeTopicSnippets
            .filter((snippet) => { var _a; return ((_a = snippet.comments) === null || _a === void 0 ? void 0 : _a.length) > 0; })
            .flatMap((snippet) => filterData(snippet.comments, query));
        if (webSearch) {
            update((state) => (Object.assign(Object.assign({}, state), { loading: true, comments: [], snippets: [], topics: [], suggestions: [] })));
        }
        else if (!semantic) {
            update((state) => (Object.assign(Object.assign({}, state), { loading: true, comments: filteredComments.length > 0 ? filteredComments : [], snippets: filteredSnippets.length > 0 ? filteredSnippets : [], topics: filteredTopics, suggestions: [] })));
        }
        update((state) => (Object.assign(Object.assign({}, state), { loading: true })));
        debouncedSearch(query, tenantId, filteredComments, filteredSnippets, filteredTopics, semantic, webSearch);
    };
    const reset = () => {
        update((state) => (Object.assign(Object.assign({}, state), { comments: [], snippets: [], topics: [], suggestions: [], loading: false, initialSearch: true, searchQuery: "" })));
    };
    const clear = () => {
        update((state) => (Object.assign(Object.assign({}, state), { comments: [], snippets: [], topics: [], suggestions: [] })));
    };
    return {
        subscribe,
        openSearchModal: (webSearch = false) => {
            // reset state when changing modes
            let state = get(GlobalSearchStore);
            if (state.webSearch !== webSearch) {
                // clear store
                clear();
                // clear input
                GlobalSearchActions.clearInput();
            }
            // open search modal
            update((state) => (Object.assign(Object.assign({}, state), { showModal: true, webSearch })));
            // focus input
            GlobalSearchActions.focusInput();
        },
        closeSearchModal: () => update((state) => (Object.assign(Object.assign({}, state), { showModal: false, webSearch: false }))),
        toggleSemanticSearch: () => update((state) => (Object.assign(Object.assign({}, state), { semanticSearch: !state.semanticSearch }))),
        setSearchQuery: (query) => update((state) => (Object.assign(Object.assign({}, state), { searchQuery: query }))),
        search,
        reset,
        clear,
    };
};
export const GlobalSearchStore = store();
